import Stack from "@mui/material/Stack";

import { toDollars, toFormattedNumber } from "../../helpers/currencies";
import { ListQuote } from "../../types/Quote";
import QuoteCard, { QuoteCardActions } from "./QuoteCard";

interface QuoteListProps {
  quotes: ListQuote[];
  primaryQuoteId: string | null;
  actions: QuoteCardActions;
  config: {
    disablePrimaryToggle?: boolean;
    hidePrimaryToggle?: boolean;
    disableCardLink?: boolean;
    hideAIReview?: boolean;
    shouldPromptForAIConsent?: boolean;
    hideClone?: boolean;
    hidePricing?: boolean;
    quotesInComparison?: string[];
    hideInvalidCalculations?: boolean;
    showInvalidCalculationToggle?: boolean;
    compareTextOverride?: string;
    highlightCompare?: boolean;
  };
}

const QuoteList = ({
  quotes,
  primaryQuoteId,
  actions,
  config,
}: QuoteListProps) => {
  const renderCard = (quote: ListQuote) => {
    return (
      <QuoteCard
        id={quote.identifiers.crmQuoteId}
        dossierId={quote.identifiers.crmDossierId}
        key={quote.identifiers.crmQuoteId}
        name={quote.identifiers.name}
        vehicleMake={quote.vehicle.make}
        vehicleModel={quote.vehicle.model}
        vehicleYear={quote.vehicle.year}
        vehicleImage={quote.vehicle.primaryImageUrl}
        vehicleOnroadCosts={toDollars(quote.vehicle.onroadCosts, 0)}
        annualKm={toFormattedNumber(quote.vehicle.budgetedAnnualKm, 0)}
        purchaseType={quote.vehicle.purchaseType}
        term={quote.term.termMonths}
        startDate={quote.term.startDate}
        endDate={quote.term.endDate}
        savings={quote.salaryComparison.savingsPercentage}
        netCosts={toDollars(
          quote.salaryComparison.netCost[quote.term.payPeriod],
          0
        )}
        calculationStatus={quote.config.calculationStatus}
        payPeriod={quote.term.payPeriod}
        catchEQuoteUrl={quote.identifiers.catchEQuoteUrl}
        actions={actions}
        config={{
          isLocked: quote.config.isLocked,
          isPrimary: quote.identifiers.crmQuoteId === primaryQuoteId,
          isVisible: quote.config.isVisible,
          isBeingCompared:
            config.quotesInComparison?.includes(quote.identifiers.crmQuoteId) ??
            false,
          disablePrimaryToggle: config.disablePrimaryToggle ?? false,
          hidePrimaryToggle: config.hidePrimaryToggle ?? false,
          disableCardLink: config.disableCardLink ?? false,
          hideClone: config.hideClone ?? false,
          hidePricing:
            (config.hidePricing ||
              (config.hideInvalidCalculations &&
                quote.config.calculationStatus === "invalid")) ??
            false,
          hideAIReview: config.hideAIReview ?? false,
          shouldPromptForAIConsent: config.shouldPromptForAIConsent ?? true,
          showInvalidCalculationToggle:
            config.showInvalidCalculationToggle ?? false,
          compareTextOverride: config.compareTextOverride ?? undefined,
          highlightCompare: config.highlightCompare ?? false,
        }}
      />
    );
  };

  return (
    <Stack
      sx={{
        display: "grid",
        gap: "16px",
        gridTemplateColumns: { md: "1fr 1fr" },
      }}
    >
      {quotes.map(renderCard)}
    </Stack>
  );
};

export default QuoteList;
